import React, {useMemo} from 'react';
import styles from './BenefactorOrdersTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Label} from '@gravity-ui/uikit';
import {DataTable} from '../../../../shared';
import {BenefactorOrderModel} from '../../model';
import {useInfiniteScroll} from '../../../../shared/hooks/useInfiniteScroll';
import {formatCurrency} from '../../../../shared';
import {formatDate} from 'date-fns';
import {getStatusBenefactorOrder} from '../../lib';
import {useNavigate} from 'react-router';

interface BenefactorOrderTableProps {
  orders: BenefactorOrderModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  benefactorId: string;
}

export const BenefactorOrdersTable = ({
  orders,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  benefactorId,
}: BenefactorOrderTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();

  const handleRowClick = (order: BenefactorOrderModel) => {
    navigate(`/accounts/benefactors/profile/${benefactorId}/orders/${order.id}`, {
      state: {benefactorId},
    });
  };

  const columns = useMemo<TableColumnConfig<BenefactorOrderModel>[]>(
    () => [
      {
        id: 'amount',
        name: 'Сумма',
        width: 225,
        template: (item: BenefactorOrderModel) => (
          <div className={styles.orderCellContainer}>
            <Text variant="body-1">{formatCurrency(item.amount)}</Text>
          </div>
        ),
      },
      {
        id: 'createdAt',
        name: 'Дата',
        width: 225,
        template: (item: BenefactorOrderModel) => (
          <div className={styles.orderCellContainer}>
            <Text variant="body-1">{formatDate(item.createdAt, 'dd.MM.yyyy HH:mm')}</Text>
          </div>
        ),
      },
      {
        id: 'status',
        name: 'Статус',
        width: 225,
        template: (item: BenefactorOrderModel) => {
          const status = getStatusBenefactorOrder(item.status);
          return (
            <div className={styles.orderCellContainer}>
              <Label theme={status.theme} size="m">
                {status.text}
              </Label>
            </div>
          );
        },
      },
      {
        id: 'failureMessage',
        name: 'Сообщение об ошибке',
        template: (item: BenefactorOrderModel) => {
          return (
            <div className={styles.orderCellContainer}>
              {item.failureMessage ? <Text variant="body-1">{item.failureMessage}</Text> : '-'}
            </div>
          );
        },
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<BenefactorOrderModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={orders || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Заказы не найдены"
        emptyStateDescription="У этого благотворителя пока нет заказов"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
